// routes.ts
import React from 'react';

// Route interface with optional title
interface Route {
  name: string;
  path: string;
  element: React.ReactNode;
  breadcrumb: string;
  title?: string;
}
import { Navigate } from 'react-router-dom';

// Simple redirect component
const TenancyManagementRedirect = () => <Navigate to="/msp-view" replace />;


// Route utility function
export const routePathByName = (name: string, params?: Record<string, string>) => {
  const route = routes.find(r => r.name === name);
  let path = route?.path;
  if (!path) return '/not-found';

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`:${key}`, value);
    }
  }
  return path;
};

// Get page title from current path with enhanced detail page support
export const getPageTitleFromPath = (pathname: string): string => {
  // Find matching route by path
  const route = routes.find(r => {
    // Handle wildcard routes (catch-all)
    if (r.path === '*') {
      return false; // Don't match wildcard routes in normal lookup
    }
    
    // Convert route path to regex pattern to handle dynamic segments
    const routePattern = r.path
      .replace(/:[^/]+/g, '[^/]+') // Replace :param with [^/]+
      .replace(/\//g, '\\/'); // Escape slashes
    
    const regex = new RegExp(`^${routePattern}$`);
    return regex.test(pathname);
  });

  if (route?.title) {
    return route.title;
  }

  // Fallback: extract from breadcrumb if no title is defined
  if (route?.breadcrumb) {
    // Get the last part of the breadcrumb (after the last >)
    const breadcrumbParts = route.breadcrumb.split(' > ');
    return breadcrumbParts[breadcrumbParts.length - 1];
  }

  // Final fallback: convert path to title case
  const path = pathname.replace(/^\//, '');
  const segments = path.split('/');
  const lastSegment = segments[segments.length - 1];
  
  if (!lastSegment) return 'Dashboard';
  
  return lastSegment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Page title context for efficient title updates without double fetching
export interface PageTitleContext {
  entityTitle?: string;
  entityId?: string;
  entityType?: 'risk' | 'document' | 'asset' | 'supplier';
  updateEntityTitle: (title: string, id: string, type: 'risk' | 'document' | 'asset' | 'supplier') => void;
  clearEntityTitle: () => void;
}

// Global state for page title (simple object-based approach)
let pageTitleState: {
  entityTitle?: string;
  entityId?: string;
  entityType?: 'risk' | 'document' | 'asset' | 'supplier';
} = {};

// Enhanced page title function that uses context instead of fetching
export const getEnhancedPageTitle = (pathname: string): string => {
  const baseTitle = getPageTitleFromPath(pathname);
  
  // Check if this is a detail page that has context information
  if (pathname.includes('/riskdetail/') || pathname.includes('/documentdetail/') || pathname.includes('/assetdetail/') || pathname.includes('/supplierdetail/')) {
    if (pageTitleState.entityTitle && pageTitleState.entityId) {
      return `${pageTitleState.entityTitle} (ID: ${pageTitleState.entityId}) | de.iterate`;
    }
  }
  
  return `${baseTitle} | de.iterate`;
};

// Function to update entity title from detail pages
export const updateEntityPageTitle = (title: string, id: string, type: 'risk' | 'document' | 'asset' | 'supplier') => {
  pageTitleState = {
    entityTitle: title,
    entityId: id,
    entityType: type
  };
  
  // Get the entity type display name
  const typeDisplayNames = {
    'risk': 'Risk Detail',
    'document': 'Document Detail', 
    'asset': 'Asset Detail',
    'supplier': 'Supplier Detail'
  };
  
  const typeDisplayName = typeDisplayNames[type];
  
  // Immediately update document title with both entity name and type
  document.title = `${title} (ID: ${id}) - ${typeDisplayName} | de.iterate`;
};

// Function to clear entity title when leaving detail pages
export const clearEntityPageTitle = () => {
  pageTitleState = {};
};

// Import components AFTER defining routes and utility functions
import { getAvailableFeatures } from '@/utils/featuresUtil';
import SOA from './pages/soa';
import SystemUpdate from '@/app/pages/system-update';
import AuditorAssuranceLibrary from './pages/auditor-view/assurance-library';
import AuditorDocumentLibrary from './pages/auditor-view/document-library';
import AuditorDocumentDetail from './pages/auditor-view/document-detail';
import ChecklistCompletePage from './pages/checklists/complete';
// TODO: Remove the old import
import { PolicyAdmin } from './pages/policy-admin';
import Settings from './pages/settings';
import ComplianceCalendar from './pages/compliance-calendar';
import NotFound from './pages/not-found';
import AuditCentre from './pages/auditcentre';
import AuditDetailPage from './pages/auditcentre/detail';
import AssetRegister from './pages/asset-register';
import SupplierRegister from './pages/supplier-register';
import AssetPage from './pages/asset-detail';
import RiskRegister from './pages/risk-register';
import RiskPage from './pages/risk-detail';
import AuditorRiskDetail from './pages/auditor-view/risk-detail';
import DocumentLibrary from './pages/documentlibrary';
import DocumentPage from './pages/document-detail';
import KeyDocuments from './pages/key-documents';
import UserManagement from './pages/user-management';
import Registers from './pages/registers'
import AssuranceLibrary from './pages/assurancelibrary';
import Dashboard from './pages/dashboard';
import TenancyManagementPage from './pages/tenancy-management';
import ManagementSystemGuide from './pages/msg';
import PrivacyRegister from './pages/privacy-register';
import ReportLibrary from './pages/report-library';
import UserReports from './pages/user-reports/page';
import Checklists from './pages/checklists/page';
import DataRetentionSchedule from './pages/data-retention-schedule';
import Examples from './pages/examples';
import MCMComboboxExamplePage from './pages/examples/mcm-combobox';
import MCMComboboxAssetsOnlyExamplePage from './pages/examples/mcm-combobox-assets-only';
import MCMComboboxSuppliersOnlyExamplePage from './pages/examples/mcm-combobox-suppliers-only';
import MCMComboboxSupportingDocsOnlyExamplePage from './pages/examples/mcm-combobox-supporting-docs-only';
// Modal example pages
import ModalTextOnlyPage from './pages/examples/modals/text-only';
import ModalTabbedPage from './pages/examples/modals/tabbed';
import ModalComponentSelectionPage from './pages/examples/modals/component-selection';
import ModalCardPickerPage from './pages/examples/modals/card-picker';
import ModalUserSelectionPage from './pages/examples/modals/user-selection';
import ModalTeamSelectionPage from './pages/examples/modals/team-selection';
import ModalConfirmationPage from './pages/examples/modals/confirmation';
import ModalFormPage from './pages/examples/modals/form';
import ModalStepperPage from './pages/examples/modals/stepper';
import ModalListPickerPage from './pages/examples/modals/list-picker';
import ModalTwoColumnPage from './pages/examples/modals/two-column';
import PolicyReaderPage from './pages/policy-reader/PolicyReaderPage';
import MasterControlMapping from './pages/mcm';
import PolicyReader from '@/app/pages/policy-reader';
import EssentialEightQuestionnaire from './pages/essential-eight';
import PrivacyActQuestionnaire from './pages/privacy-act';
import TemplateLibrary from './pages/template-library';
import AITestPage from './pages/ai-test';
import SOC2ControlMatrixPage from './pages/soc2-control-matrix';
import Duck from './pages/duck';  

const hasExamplesFeature = !!getAvailableFeatures().find(f => f.name === 'Examples');

export const routes: Route[] = [
  { name: 'duck', path: '/duck', element: <Duck />, breadcrumb: 'Duck', title: 'Duck' },
  { name: 'checklists.complete', path: '/checklists/complete/:id', element: <ChecklistCompletePage />, breadcrumb: 'Checklist Completion', title: 'Checklist Completion' },
  { name: 'mcm', path: '/mcm', element: <MasterControlMapping />, breadcrumb: 'Master Control Mapping', title: 'Master Control Mapping' },
  // Main MSG route
  { name: 'msg', path: '/msg', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide' },

  // Overview
  { name: 'msg.overview', path: '/msg/overview', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Overview' },

  // Context section
  { name: 'msg.context', path: '/msg/context', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context' },
  { name: 'msg.context.issues', path: '/msg/context/issues', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Issues' },
  { name: 'msg.context.issues.item', path: '/msg/context/issues/:id', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Issues' },
  { name: 'msg.context.parties', path: '/msg/context/parties', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Interested Parties' },
  { name: 'msg.context.parties.item', path: '/msg/context/parties/:id', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Interested Parties' },
  { name: 'msg.context.scope', path: '/msg/context/scope', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Scope' },
  { name: 'msg.context.mms', path: '/msg/context/mms', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > MMS' },
  { name: 'msg.context.legal', path: '/msg/context/legal', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Legal Obligations' },
  { name: 'msg.context.legal.item', path: '/msg/context/legal/:id', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Legal Obligations' },
  { name: 'msg.context.groups', path: '/msg/context/groups', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Context > Special Interest Groups' },

  // Leadership section
  { name: 'msg.leadership', path: '/msg/leadership', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Leadership' },
  { name: 'msg.leadership.commitment', path: '/msg/leadership/commitment', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Leadership > Commitment' },
  { name: 'msg.leadership.policy', path: '/msg/leadership/policy', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Leadership > Policy' },
  { name: 'msg.leadership.roles', path: '/msg/leadership/roles', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Leadership > Roles' },

  // Planning section
  { name: 'msg.planning', path: '/msg/planning', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning' },
  { name: 'msg.planning.general', path: '/msg/planning/general', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning > General' },
  { name: 'msg.planning.changes', path: '/msg/planning/changes', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning > Changes' },
  { name: 'msg.planning.objectives', path: '/msg/planning/objectives', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning > Objectives' },
  { name: 'msg.planning.objectives.item', path: '/msg/planning/objectives/:id', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning > Objectives' },
  { name: 'msg.planning.risk-assessment', path: '/msg/planning/risk-assessment', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning > Risk Assessment' },
  { name: 'msg.planning.risk-treatment', path: '/msg/planning/risk-treatment', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Planning > Risk Treatment' },

  // Operation section
  { name: 'msg.operation', path: '/msg/operation', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Operation' },
  { name: 'msg.operation.overview', path: '/msg/operation/overview', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Operation > Overview' },

  // Performance section
  { name: 'msg.performance', path: '/msg/performance', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Performance' },
  { name: 'msg.performance.monitoring', path: '/msg/performance/monitoring', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Performance > Monitoring' },
  { name: 'msg.performance.internalaudit', path: '/msg/performance/internalaudit', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Performance > Internal Audit' },
  { name: 'msg.performance.managementreview', path: '/msg/performance/managementreview', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Performance > Management Review' },

  // Support section
  { name: 'msg.support', path: '/msg/support', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support' },
  { name: 'msg.support.communication', path: '/msg/support/communication', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Communication' },
  { name: 'msg.support.documentation', path: '/msg/support/documentation', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Documentation' },
  { name: 'msg.support.roles', path: '/msg/support/roles', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Roles' },

  // Support > Competency subsection
  { name: 'msg.support.competency', path: '/msg/support/competency', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Competency' },
  { name: 'msg.support.competency.overview', path: '/msg/support/competency/overview', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Competency > Overview' },
  { name: 'msg.support.competency.assetOwner', path: '/msg/support/competency/assetOwner', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Competency > Asset Owner' },
  { name: 'msg.support.competency.top-management', path: '/msg/support/competency/top-management', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Competency > Top Management' },
  { name: 'msg.support.competency.user', path: '/msg/support/competency/user', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Competency > User' },
  { name: 'msg.support.competency.business-sponsor', path: '/msg/support/competency/business-sponsor', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Support > Competency > Business Sponsor' },

  // Improvement section
  { name: 'msg.improvements', path: '/msg/improvements', element: <ManagementSystemGuide />, breadcrumb: 'Management System Guide > Improvement' },

  { name: 'dashboard', path: '/dashboard', element: <Dashboard />, breadcrumb: 'Dashboard', title: 'Dashboard' },
  { name: 'auditcentre', path: '/auditcentre', element: <AuditCentre />, breadcrumb: 'Audit Centre', title: 'Audit Centre' },
  { name: 'auditcentre.audits', path: '/auditcentre/audits', element: <AuditCentre />, breadcrumb: 'Audit Centre > Audits' },
  { name: 'auditcentre.findings', path: '/auditcentre/findings', element: <AuditCentre />, breadcrumb: 'Audit Centre > Findings' },
  { name: 'auditcentre.incidents', path: '/auditcentre/incidents', element: <AuditCentre />, breadcrumb: 'Audit Centre > Incidents' },
  { name: 'auditcentre.corrective-actions', path: '/auditcentre/corrective-actions', element: <AuditCentre />, breadcrumb: 'Audit Centre > Corrective Actions' },
  { name: 'auditcentre.detail', path: '/auditcentre/:id', element: <AuditDetailPage />, breadcrumb: 'Audit Detail' },
  { name: 'soa', path: '/soa', element: <SOA />, breadcrumb: 'Statement of Applicability' },
  { name: 'system-update', path: '/system-update', element: <SystemUpdate />, breadcrumb: 'System Update' },
  { name: 'checklists', path: '/checklists', element: <Checklists />, breadcrumb: 'Checklists' },
  { name: 'checklists.item', path: '/checklists/:id', element: <Checklists />, breadcrumb: 'Checklists' },
  { name: 'policy-admin', path: '/policyadmin', element: <PolicyAdmin />, breadcrumb: 'Policy Admin' },
  { name: 'policy-admin.edit', path: '/policyadmin/:policyId', element: <PolicyAdmin />, breadcrumb: 'Policy Admin' },
  { name: 'policies', path: '/policies', element: <PolicyAdmin initialFilter="policy" />, breadcrumb: 'Policies' },
  { name: 'processes', path: '/processes', element: <PolicyAdmin initialFilter="procedure" />, breadcrumb: 'Processes' },
  { name: 'privacy-request-register', path: '/privacy-request-register', element: <PrivacyRegister />, breadcrumb: 'Privacy Request Register' },
  { name: 'privacy-request-register.item', path: '/privacy-request-register/:id', element: <PrivacyRegister />, breadcrumb: 'Privacy Request Register' },
  { name: 'dataRegister', path: '/dataRegister', element: <DataRetentionSchedule />, breadcrumb: 'Data Retention Schedule' },
  { name: 'dataRegister.item', path: '/dataRegister/:id', element: <DataRetentionSchedule />, breadcrumb: 'Data Retention Schedule' },
  { name: 'compliance-calendar', path: '/compliance-calendar', element: <ComplianceCalendar />, breadcrumb: 'Compliance Calendar' },
  { name: 'settings', path: '/settings', element: <Settings />, breadcrumb: 'Settings', title: 'Settings' },
  { name: 'settings.theme', path: '/settings/theme', element: <Settings />, breadcrumb: 'Settings > Theme' },
  { name: 'settings.account', path: '/settings/account', element: <Settings />, breadcrumb: 'Settings > Account' },
  { name: 'settings.security', path: '/settings/security', element: <Settings />, breadcrumb: 'Settings > Security' },
  { name: 'settings.general', path: '/settings/general', element: <Settings />, breadcrumb: 'Settings > General' },
  { name: 'settings.calendar', path: '/settings/calendar', element: <Settings />, breadcrumb: 'Settings > Calendar' },
  { name: 'settings.billing', path: '/settings/billing', element: <Settings />, breadcrumb: 'Settings > Billing' },
  { name: 'settings.integrations', path: '/settings/integrations', element: <Settings />, breadcrumb: 'Settings > Integrations' },
  { name: 'settings.modules', path: '/settings/modules', element: <Settings />, breadcrumb: 'Settings > Modules' },
  { name: 'settings.features', path: '/settings/features', element: <Settings />, breadcrumb: 'Settings > Features' },
  { name: 'settings.trustPage', path: '/settings/trustPage', element: <Settings />, breadcrumb: 'Settings > Trust Page' },
  { name: 'settings.partners', path: '/settings/partners', element: <Settings />, breadcrumb: 'Settings > Partners' },
  { name: 'settings.mspSettings', path: '/settings/mspSettings', element: <Settings />, breadcrumb: 'Settings > MSP Settings' },
  { name: 'settings.menus', path: '/settings/menus', element: <Settings />, breadcrumb: 'Settings > Menus' },
  { name: 'settings.diagnostics', path: '/settings/diagnostics', element: <Settings />, breadcrumb: 'Settings > Diagnostics' },
  { name: 'settings.developer', path: '/settings/developer', element: <Settings />, breadcrumb: 'Settings > Developer' },
  { name: 'settings.authorisedDomains', path: '/settings/authorisedDomains', element: <Settings />, breadcrumb: 'Settings > Authorised Domains', title: 'Authorised Domains' },
  { name: 'settings.ssoProviders', path: '/settings/ssoProviders', element: <Settings />, breadcrumb: 'Settings > SSO Providers', title: 'SSO Providers' },
  { name: 'settings.joinRequests', path: '/settings/joinRequests', element: <Settings />, breadcrumb: 'Settings > Join Requests', title: 'Join Requests' },
  { name: 'asset-register', path: '/asset-register', element: <AssetRegister registerType="Asset" />, breadcrumb: 'Asset Register', title: 'Asset Register' },
  { name: 'asset.detail', path: '/assetdetail/:id', element: <AssetPage />, breadcrumb: 'Asset Detail', title: 'Asset Detail' },
  // DED-1535: Supplier Register routes
  { name: 'supplier-register', path: '/supplier-register', element: <SupplierRegister />, breadcrumb: 'Supplier Register', title: 'Supplier Register' },
  { name: 'supplier.detail', path: '/supplierdetail/:id', element: <AssetPage />, breadcrumb: 'Supplier Detail', title: 'Supplier Detail' },
  { name: 'risk-register', path: '/risk-register', element: <RiskRegister />, breadcrumb: 'Risk Register', title: 'Risk Register' },
  { name: 'risk.detail', path: '/riskdetail/:id', element: <RiskPage />, breadcrumb: 'Risk Detail', title: 'Risk Details' },
  { name: 'auditor.risk-detail', path: '/auditor-view/risk-detail/:id', element: <AuditorRiskDetail />, breadcrumb: 'Auditor Risk Detail', title: 'Risk Details (Auditor)' },
  { name: 'report-library', path: '/report-library', element: <ReportLibrary />, breadcrumb: 'Report Library', title: 'Report Library' },
  { name: 'template-library', path: '/template-library', element: <TemplateLibrary />, breadcrumb: 'Template Library', title: 'Template Library' },
  { name: 'documentlibrary', path: '/documentlibrary', element: <DocumentLibrary />, breadcrumb: 'Document Library', title: 'Document Library' },
  { name: 'auditor-view.document-library', path: '/auditor-view/document-library', element: <AuditorDocumentLibrary />, breadcrumb: 'Auditor View > Document Library', title: 'Document Library (Auditor)' },
  { name: 'auditor-view.document-detail', path: '/auditor-view/document-detail/:id', element: <AuditorDocumentDetail />, breadcrumb: 'Auditor View > Document Detail', title: 'Document Details (Auditor)' },
  { name: 'documentdetail', path: '/documentdetail/:id', element: <DocumentPage />, breadcrumb: 'Document Detail', title: 'Document Details' },
  { name: 'key-documents', path: '/key-documents', element: <KeyDocuments />, breadcrumb: 'Key Documents', title: 'Key Documents'},
  { name: 'user-management', path: '/usermanagement', element: <UserManagement />, breadcrumb: 'User Management', title: 'User Management'},
  { name: 'assurance-library', path: '/assurancelibrary', element: <AssuranceLibrary />, breadcrumb: 'Assurance Library', title: 'Assurance Library'},
  { name: 'soc2-control-matrix', path: '/controlMatrix', element: <SOC2ControlMatrixPage />, breadcrumb: 'SOC 2 Control Matrix', title: 'SOC 2 Control Matrix'},
  { name: 'auditor-view.assurance-library', path: '/auditor-view/assurance-library', element: <AuditorAssuranceLibrary />, breadcrumb: 'Auditor Assurance Library', title: 'Assurance Library (Auditor)'},
  { name: 'registers', path: '/registers', element: <Registers />, breadcrumb: 'Registers', title: 'Registers'},
  { name: 'reports', path: '/reports', element: <UserReports />, breadcrumb: 'User Reports', title: 'Reports'},
  { name: 'msp-view', path: '/msp-view', element: <TenancyManagementPage />, breadcrumb: 'Tenancy Management', title: 'Tenancy Management' },
  { name: 'tenancy-management', path: '/tenancy-management', element: <TenancyManagementRedirect />, breadcrumb: 'Tenancy Management', title: "Tenancy Management" },
  ...(hasExamplesFeature ? [
    { name: 'examples', path: '/examples', element: <Examples />, breadcrumb: 'Examples' },
    { name: 'examples.mcm-combobox', path: '/examples/mcm-combobox', element: <MCMComboboxExamplePage />, breadcrumb: 'Examples > MCM Combobox' },
    { name: 'examples.mcm-combobox-assets-only', path: '/examples/mcm-combobox-assets-only', element: <MCMComboboxAssetsOnlyExamplePage />, breadcrumb: 'Examples > MCM Combobox (Assets Only)' },
    { name: 'examples.mcm-combobox-suppliers-only', path: '/examples/mcm-combobox-suppliers-only', element: <MCMComboboxSuppliersOnlyExamplePage />, breadcrumb: 'Examples > MCM Combobox (Suppliers Only)' },
    { name: 'examples.mcm-combobox-supporting-docs-only', path: '/examples/mcm-combobox-supporting-docs-only', element: <MCMComboboxSupportingDocsOnlyExamplePage />, breadcrumb: 'Examples > MCM Combobox (Supporting Docs Only)' },
    // Modal examples
    { name: 'examples.modals.text-only', path: '/examples/modals/text-only', element: <ModalTextOnlyPage />, breadcrumb: 'Examples > Modals > Text Only' },
    { name: 'examples.modals.tabbed', path: '/examples/modals/tabbed', element: <ModalTabbedPage />, breadcrumb: 'Examples > Modals > Tabbed' },
    { name: 'examples.modals.component-selection', path: '/examples/modals/component-selection', element: <ModalComponentSelectionPage />, breadcrumb: 'Examples > Modals > Component Selection' },
    { name: 'examples.modals.card-picker', path: '/examples/modals/card-picker', element: <ModalCardPickerPage />, breadcrumb: 'Examples > Modals > Card Picker' },
    { name: 'examples.modals.user-selection', path: '/examples/modals/user-selection', element: <ModalUserSelectionPage />, breadcrumb: 'Examples > Modals > User Selection' },
    { name: 'examples.modals.team-selection', path: '/examples/modals/team-selection', element: <ModalTeamSelectionPage />, breadcrumb: 'Examples > Modals > Team Selection' },
    { name: 'examples.modals.confirmation', path: '/examples/modals/confirmation', element: <ModalConfirmationPage />, breadcrumb: 'Examples > Modals > Confirmation' },
    { name: 'examples.modals.form', path: '/examples/modals/form', element: <ModalFormPage />, breadcrumb: 'Examples > Modals > Form' },
    { name: 'examples.modals.stepper', path: '/examples/modals/stepper', element: <ModalStepperPage />, breadcrumb: 'Examples > Modals > Stepper' },
    { name: 'examples.modals.list-picker', path: '/examples/modals/list-picker', element: <ModalListPickerPage />, breadcrumb: 'Examples > Modals > List Picker' },
    { name: 'examples.modals.two-column', path: '/examples/modals/two-column', element: <ModalTwoColumnPage />, breadcrumb: 'Examples > Modals > Two Column' },
  ] : []),
  { name: 'ai-test', path: '/ai-test', element: <AITestPage />, breadcrumb: 'AI Test', title: 'AI Test' },
  { name: 'policy-reader-index', path: '/policy-reader', element: <PolicyReader />, breadcrumb: 'Policy Reader', title: 'Policy Reader' },
  { name: 'policy-reader', path: '/policy-reader/:policyId', element: <PolicyReaderPage />, breadcrumb: 'Policy Reader', title: 'Policy Reader' },
  { name: 'essential-eight', path: '/essential-eight', element: <EssentialEightQuestionnaire />, breadcrumb: 'Essential Eight Questionnaire', title: 'Essential Eight' },
  { name: 'privacy-act', path: '/privacy-act', element: <PrivacyActQuestionnaire />, breadcrumb: 'Privacy Act Questionnaire', title: 'Privacy Act' },
  { name: 'not-found', path: '*', element: <NotFound />, breadcrumb: 'Not Found', title: 'Page Not Found' }
];
